import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewsletterSubscribe — compact email opt-in that fires every Monday's
 * weekly gig digest to the subscriber. Drop-in anywhere; passes `source`
 * so we can track which page converted best (family / contest / footer).
 *
 * Deliberately minimal — one input, one button, one toast. No modal,
 * no privacy policy dialog. Users on this page already understand DRR.
 */
export default function NewsletterSubscribe({
  source = "family",
  className = "",
  compact = false,
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [subNumber, setSubNumber] = useState(null);
  const [wasAlready, setWasAlready] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        source,
      });
      const status = res?.data?.status;
      const number = res?.data?.number;
      setSubNumber(typeof number === "number" ? number : null);
      setWasAlready(status === "already_subscribed");
      setDone(true);
      toast.success(
        status === "already_subscribed"
          ? `🤘 Already subscriber #${number} — see you Monday`
          : `📬 Locked in as subscriber #${number} — welcome to the pit`,
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't subscribe — try again in a sec";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className={`inline-flex items-stretch border-2 border-red-600 bg-red-600/10 ${className}`}
        data-testid="newsletter-subscribed"
      >
        {subNumber !== null && (
          <div
            className="bg-red-600 text-black px-3 py-2 flex flex-col items-center justify-center min-w-[68px]"
            data-testid="newsletter-subscriber-badge"
          >
            <div className="text-[9px] tracking-[0.25em] uppercase font-display leading-none">
              SUB #
            </div>
            <div className="font-display text-xl leading-none tabular-nums mt-0.5">
              {subNumber}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 px-3 py-2">
          <CheckCircle2 className="w-4 h-4 text-red-400 shrink-0" strokeWidth={2.5} />
          <span className="text-xs md:text-sm tracking-widest uppercase font-display text-red-300 leading-tight">
            {wasAlready
              ? "Already in. See you Monday."
              : "Welcome to the pit. See you Monday."}
          </span>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`flex flex-col md:flex-row items-stretch gap-0 max-w-md ${className}`}
      data-testid="newsletter-form"
    >
      <div className="flex-1 flex items-center gap-2 border-2 border-zinc-700 focus-within:border-red-600 bg-black px-3 transition-colors">
        <Mail className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2.5} />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@somewhere.loud"
          maxLength={240}
          required
          className={`flex-1 min-w-0 bg-transparent outline-none text-zinc-50 font-display ${
            compact ? "py-2 text-sm" : "py-3 text-sm md:text-base"
          }`}
          data-testid="newsletter-email-input"
        />
      </div>
      <button
        type="submit"
        disabled={submitting || !email.trim()}
        className={`inline-flex items-center justify-center gap-2 border-2 border-t-0 md:border-t-2 md:border-l-0 border-red-600 tracking-widest uppercase font-display transition-colors ${
          submitting || !email.trim()
            ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
            : "bg-red-600 text-white hover:bg-white hover:text-black"
        } ${compact ? "px-4 py-2 text-xs" : "px-5 py-3 text-xs md:text-sm"}`}
        data-testid="newsletter-submit"
      >
        {submitting ? (
          <span className="inline-block w-3 h-3 border-2 border-current border-r-transparent rounded-full animate-spin" />
        ) : (
          <Send className="w-3 h-3" strokeWidth={2.5} />
        )}
        {submitting ? "…" : "SUBSCRIBE"}
      </button>
    </form>
  );
}
